import React from "react";
import { Heart, Calendar, Clock, MapPin } from "lucide-react";

const PLAYFAIR = "'Playfair Display', Georgia, serif";
const CORMORANT = "'Cormorant Garamond', Georgia, serif";
const GREATVIBES = "'Great Vibes', cursive";
const DANCING = "'Dancing Script', cursive";
const MARCELLUS = "'Marcellus', serif";
const TANGERINE = "'Tangerine', cursive";
const POPPINS = "'Poppins', sans-serif";

// 20 el yapımı davetiye teması
export const INVITATION_TEMPLATES = [
  { id: "classic-rose", name: "Klasik Gül", bg: "#FBF5F0", accent: "#9E2A3B", text: "#2A2224", sub: "#7C6E70", heading: PLAYFAIR, layout: "centered", frame: true },
  { id: "gold-luxe", name: "Altın Lüks", bg: "#14213D", accent: "#E3C56B", text: "#F5EFE0", sub: "#C9B98A", heading: CORMORANT, layout: "monogram", frame: true },
  { id: "blush-script", name: "Pudra Zarafet", bg: "#F7E7E6", accent: "#C15B6B", text: "#5A3B3F", sub: "#9A7379", heading: GREATVIBES, layout: "centered", frame: false },
  { id: "emerald-garden", name: "Zümrüt Bahçe", bg: "#0E3B32", accent: "#D9B26A", text: "#EAF3EE", sub: "#A9C7B9", heading: CORMORANT, layout: "centered", frame: true },
  { id: "minimal-mono", name: "Minimal", bg: "#FFFFFF", accent: "#1A1A1A", text: "#1A1A1A", sub: "#8A8A8A", heading: MARCELLUS, layout: "topbar", frame: false },
  { id: "dusty-blue", name: "Puslu Mavi", bg: "#DCE6EC", accent: "#3E5C76", text: "#22333B", sub: "#5E7385", heading: CORMORANT, layout: "centered", frame: true },
  { id: "terracotta", name: "Toprak", bg: "#C9663E", accent: "#F3E6D0", text: "#FDF3EA", sub: "#F0D4BE", heading: PLAYFAIR, layout: "monogram", frame: false },
  { id: "lavender", name: "Lavanta", bg: "#EAE3F2", accent: "#7B5EA7", text: "#3E2C55", sub: "#8672A0", heading: DANCING, layout: "centered", frame: false },
  { id: "burgundy-gold", name: "Bordo & Altın", bg: "#4A1420", accent: "#D9B26A", text: "#F6E9DE", sub: "#C9A08A", heading: PLAYFAIR, layout: "monogram", frame: true },
  { id: "sage-cream", name: "Adaçayı", bg: "#DDE3D0", accent: "#5B6B47", text: "#3A4230", sub: "#788063", heading: CORMORANT, layout: "centered", frame: true },
  { id: "midnight-floral", name: "Gece Çiçeği", bg: "#1C1B22", accent: "#E0A3B0", text: "#F1E9EC", sub: "#B49AA2", heading: GREATVIBES, layout: "centered", frame: true },
  { id: "peach-modern", name: "Şeftali", bg: "#FFE6D6", accent: "#E8714E", text: "#5E3A2A", sub: "#A87862", heading: POPPINS, layout: "topbar", frame: false },
  { id: "royal-purple", name: "Kraliyet Moru", bg: "#3B2159", accent: "#E3C56B", text: "#F1EAF7", sub: "#BBA6D0", heading: MARCELLUS, layout: "monogram", frame: true },
  { id: "ocean-teal", name: "Okyanus", bg: "#0F4C5C", accent: "#F2E9D8", text: "#EAF6F7", sub: "#A7CBD1", heading: CORMORANT, layout: "centered", frame: true },
  { id: "rustic-kraft", name: "Rustik", bg: "#B9925E", accent: "#3E2C1C", text: "#2E1F12", sub: "#5E4A32", heading: PLAYFAIR, layout: "topbar", frame: true },
  { id: "pink-gold", name: "Pembe Altın", bg: "#F6D9DE", accent: "#B98A3E", text: "#6E3B44", sub: "#A67680", heading: TANGERINE, layout: "centered", frame: false },
  { id: "champagne", name: "Şampanya", bg: "#EFE4D2", accent: "#A98F5F", text: "#5A4B36", sub: "#8C7A5E", heading: CORMORANT, layout: "monogram", frame: true },
  { id: "red-classic", name: "Kırmızı Klasik", bg: "#FBF3EE", accent: "#C0392B", text: "#3A2320", sub: "#8A6E68", heading: PLAYFAIR, layout: "centered", frame: true },
  { id: "botanical-white", name: "Botanik", bg: "#F7F8F4", accent: "#4E7A51", text: "#2E3A2C", sub: "#6E7C68", heading: MARCELLUS, layout: "centered", frame: false },
  { id: "sunset", name: "Gün Batımı", bg: "linear-gradient(160deg,#F6A96B 0%,#C85C7E 55%,#7A4A8C 100%)", accent: "#FFF3E6", text: "#FFFFFF", sub: "#F3E0EA", heading: PLAYFAIR, layout: "centered", frame: false },
];

export const getTemplate = (id) =>
  INVITATION_TEMPLATES.find((t) => t.id === id) || INVITATION_TEMPLATES[0];

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

export const formatInvDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()} ${DAYS[dt.getDay()]}`;
};

const initials = (a, b) => `${(a || "").trim().charAt(0)}${(b || "").trim().charAt(0)}`.toUpperCase();

export const InvitationCard = ({ template, data = {}, compact = false }) => {
  const t = typeof template === "string" ? getTemplate(template) : template;
  const bride = data.brideName || "Gelin";
  const groom = data.groomName || "Damat";
  const isGradient = String(t.bg).startsWith("linear-gradient");
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const photoSrc = data.photo ? (data.photo.startsWith("http") ? data.photo : BACKEND + data.photo) : "";

  const pad = compact ? "p-3" : "p-6 sm:p-10";
  const nameSize = compact ? "text-lg" : "text-4xl sm:text-5xl";
  const kickerSize = compact ? "text-[8px]" : "text-xs";

  return (
    <div
      data-testid="invitation-card"
      className={`w-full h-full ${pad} flex items-center justify-center text-center`}
      style={{ background: t.bg, color: t.text, fontFamily: POPPINS }}
    >
      <div
        className={`w-full ${compact ? "" : "max-w-md mx-auto"} ${t.frame ? (compact ? "border p-3" : "border-2 p-6 sm:p-8") : ""} rounded-lg`}
        style={t.frame ? { borderColor: t.accent } : {}}
      >
        {photoSrc && (
          <img
            src={photoSrc}
            alt={`${bride} & ${groom}`}
            className={`mx-auto rounded-full object-cover ${compact ? "mb-2" : "mb-4"}`}
            style={{ width: compact ? 44 : 120, height: compact ? 44 : 120, border: `3px solid ${t.accent}` }}
          />
        )}
        {/* top decoration */}
        {!photoSrc && t.layout === "topbar" && (
          <div className="mx-auto mb-4" style={{ width: compact ? 30 : 64, height: 4, background: t.accent, borderRadius: 4 }} />
        )}
        {!photoSrc && t.layout === "monogram" && (
          <div
            className={`mx-auto flex items-center justify-center rounded-full ${compact ? "mb-2" : "mb-5"}`}
            style={{ width: compact ? 28 : 64, height: compact ? 28 : 64, border: `2px solid ${t.accent}`, color: t.accent, fontFamily: t.heading, fontSize: compact ? 12 : 24 }}
          >
            {initials(bride, groom) || "♥"}
          </div>
        )}
        {!photoSrc && t.layout === "centered" && (
          <div className={`flex items-center justify-center gap-2 ${compact ? "mb-1" : "mb-3"}`}>
            <span style={{ height: 1, width: compact ? 16 : 40, background: t.accent, display: "inline-block" }} />
            <Heart className={compact ? "w-3 h-3" : "w-5 h-5"} style={{ color: t.accent }} fill={t.accent} />
            <span style={{ height: 1, width: compact ? 16 : 40, background: t.accent, display: "inline-block" }} />
          </div>
        )}

        <p className={`${kickerSize} tracking-[0.3em] uppercase`} style={{ color: t.sub }}>
          Düğünümüze Davetlisiniz
        </p>

        <h1 className={`${nameSize} leading-tight ${compact ? "my-1" : "my-3"}`} style={{ fontFamily: t.heading, color: t.text }}>
          {bride}
          <span className={compact ? "block text-sm" : "block text-2xl sm:text-3xl my-1"} style={{ color: t.accent, fontFamily: t.heading }}>&</span>
          {groom}
        </h1>

        {!compact && (
          <>
            <div className="mx-auto my-4" style={{ width: 50, height: 1, background: t.accent }} />

            <div className="space-y-2 text-sm" style={{ color: t.sub }}>
              {data.date && (
                <p className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color: t.accent }} /> {formatInvDate(data.date)}
                </p>
              )}
              {data.time && (
                <p className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" style={{ color: t.accent }} /> {data.time}
                </p>
              )}
              {(data.venue || data.city) && (
                <p className="flex items-center justify-center gap-2 font-medium" style={{ color: t.text }}>
                  <MapPin className="w-4 h-4" style={{ color: t.accent }} /> {[data.venue, data.city].filter(Boolean).join(", ")}
                </p>
              )}
              {data.address && <p className="text-xs">{data.address}</p>}
            </div>

            {data.note && (
              <p className="mt-5 text-sm italic" style={{ fontFamily: t.heading, color: t.text }}>
                “{data.note}”
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvitationCard;
